/**
 *
 * TripTitleHeader
 *
 */

import { CalendarMonthOutlined, EditOutlined, IosShare, MoreHoriz } from '@mui/icons-material';
import { ClickAwayListener } from '@mui/material';
import { addDays, getYear, parseISO } from 'date-fns';
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_START_DATE } from '../../../constants';
import { TripRole } from '../../../firebase/firebase.types';
import useAnchorMenu from '../../../hooks/useAnchorMenu';
import useHover from '../../../hooks/useHover';
import useMobileMediaQuery from '../../../hooks/useMobileMediaQuery';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateDates } from '../../../redux/trip/trip.operations';
import { selectUserEmails } from '../../../redux/trip/trip.selectors';
import { updateTrip } from '../../../redux/trip/trip.slice';
import { Trip } from '../../../redux/trip/trip.types';
import getItineraryTitle from '../../../utils/getItineraryTitle';
import DateRangePicker from '../../DateRangePicker/DateRangePicker';
import LetterAvatarGroup from '../../LetterAvatar/LetterAvatarGroup';
import { DropdownMenu, IconButton } from '../../shared';
import TripShare from '../TripShare/TripShare';
import './TripTitleHeader.scss';

type TripTitleHeaderProps = {
  trip: Trip;
  canEdit?: boolean;
  currentUser: unknown;
};

const placeCaretAtEnd = (tripTitleSpan: HTMLSpanElement) => {
  const range = document.createRange();
  const selection = window.getSelection();
  range.selectNodeContents(tripTitleSpan);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
};

function TripTitleHeader({ trip, canEdit = false, currentUser }: TripTitleHeaderProps) {
  const { id: tripId, title: tripTitle, place } = trip;
  const dispatch = useAppDispatch();
  const isMobile = useMobileMediaQuery();
  const userRoles = useAppSelector(selectUserEmails) as TripRole[];

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const { isHovered: isDateHovered, hoverProps: dateHoverProps } = useHover();
  const [isEditing, setIsEditing] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const titleRef = useRef<HTMLSpanElement>(null);
  const menu = useAnchorMenu<HTMLButtonElement>();
  const [characterCount, setCharacterCount] = useState(tripTitle.length);

  const maxCharacterCount = 40;

  const defaultNumDays = 4;

  const startDate = useMemo(
    () => (trip.startDateISO ? parseISO(trip.startDateISO) : DEFAULT_START_DATE),
    [trip.startDateISO]
  );
  const endDate = useMemo(
    () =>
      trip.endDateISO ? parseISO(trip.endDateISO) : addDays(DEFAULT_START_DATE, defaultNumDays - 1),
    [trip.endDateISO]
  );
  const tripDateRange = useMemo(() => ({ from: startDate, to: endDate }), [startDate, endDate]);

  const isDefaultYear = useMemo(
    () => getYear(tripDateRange?.from || DEFAULT_START_DATE) === getYear(DEFAULT_START_DATE),
    [tripDateRange]
  );

  // Split on the range separator so a long label wraps between the two dates, not inside one.
  const dateLabelParts = (
    isDefaultYear
      ? getItineraryTitle(undefined, isMobile)
      : getItineraryTitle(tripDateRange, isMobile)
  ).split(' - ');

  const setDefaultDates = useCallback(() => {
    dispatch(
      updateDates({
        tripId: trip.id,
        oldDateRange: tripDateRange,
        newDateRange: { from: startDate, to: endDate },
        days: trip.days,
      })
    );
  }, [dispatch, trip.id, trip.days, startDate, endDate, tripDateRange]);

  // Seed placeholder dates once per trip; the store has them only after the write completes, so
  // without the guard every re-render in between would dispatch another write.
  const seededTripId = useRef<string | null>(null);
  useEffect(() => {
    if (startDate !== DEFAULT_START_DATE || seededTripId.current === tripId) {
      return;
    }
    seededTripId.current = tripId;
    setDefaultDates();
  }, [setDefaultDates, startDate, tripId]);

  const startEditing = (tripTitleSpan: HTMLSpanElement) => {
    if (!isEditing) {
      setIsEditing(true);
      setCharacterCount(tripTitleSpan.innerText.length);
      tripTitleSpan.focus();
      placeCaretAtEnd(tripTitleSpan);
    }
  };

  const makeEditable = (event: MouseEvent<HTMLSpanElement>) =>
    startEditing(event.target as HTMLSpanElement);

  useEffect(() => {
    if (isMobile && isEditing && titleRef.current) {
      titleRef.current.focus();
      placeCaretAtEnd(titleRef.current);
    }
  }, [isMobile, isEditing]);

  const handleInput = (event: React.FormEvent<HTMLSpanElement>) => {
    const tripTitleSpan = event.target as HTMLSpanElement;
    const text = tripTitleSpan.innerText;
    setCharacterCount(text.length);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLSpanElement>) => {
    const tripTitleSpan = event.target as HTMLSpanElement;
    const currentText = tripTitleSpan.innerText;
    const pastedText = event.clipboardData.getData('text/plain');
    const selection = window.getSelection();
    const selectionLength = selection?.toString().length || 0;
    const newLength = currentText.length - selectionLength + pastedText.length;

    if (newLength > maxCharacterCount) {
      event.preventDefault();
      // Allow paste up to the max limit
      const allowedLength = maxCharacterCount - (currentText.length - selectionLength);
      const trimmedText = pastedText.substring(0, allowedLength);
      document.execCommand('insertText', false, trimmedText);
    }
  };

  const submitChanges = (
    event: React.FocusEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>
  ) => {
    const tripTitleSpan = event.target as HTMLSpanElement;
    setIsEditing(false);
    const text = tripTitleSpan.innerText;

    if (text.length === 0 || !text.trim() || text === tripTitle) {
      tripTitleSpan.innerText = tripTitle;
      return;
    }

    dispatch(updateTrip({ tripId, field: 'title', value: text }));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    const tripTitleSpan = event.target as HTMLSpanElement;
    const text = tripTitleSpan.innerText;

    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent creating a new line
      submitChanges(event);
    }

    // Allow navigation and editing keys even when at max length
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

    if (
      text.length >= maxCharacterCount &&
      !allowedKeys.includes(event.key) &&
      !event.metaKey &&
      !event.ctrlKey
    ) {
      event.preventDefault(); // Prevent further input
    }
  };

  return (
    <div>
      <div className='trip-title-input-wrapper'>
        <span
          ref={titleRef}
          className={`trip-title-header-text${canEdit ? ' editable' : ''}`}
          contentEditable={canEdit && (!isMobile || isEditing)}
          onClick={canEdit && !isMobile ? makeEditable : undefined}
          onBlur={canEdit ? submitChanges : undefined}
          onInput={canEdit ? handleInput : undefined}
          onPaste={canEdit ? handlePaste : undefined}
          onKeyDown={canEdit ? handleKeyDown : undefined}
          suppressContentEditableWarning
        >
          {tripTitle}
        </span>
        {canEdit && isEditing && (
          <div className='character-counter'>
            <span>
              {characterCount}/{maxCharacterCount}
            </span>
          </div>
        )}
      </div>
      <div className='trip-location-dates-row'>
        <div className='trip-location-text'>{`📍 ${place}`}</div>
        {(!canEdit && isDefaultYear) || (isMobile && isDefaultYear) ? null : (
          <ClickAwayListener onClickAway={() => !isMobile && setIsDatePickerOpen(false)}>
            <div
              className='trip-dates-container'
              {...dateHoverProps}
            >
              <span
                className={`trip-date-text${canEdit ? ' clickable' : ''}`}
                onClick={canEdit ? () => setIsDatePickerOpen((prev) => !prev) : undefined}
              >
                {dateLabelParts.map((part, index) => (
                  <span
                    key={part}
                    className='trip-date-part'
                  >
                    {index === 0 ? '🗓️ ' : ' '}
                    {index < dateLabelParts.length - 1 ? `${part} -` : part}
                  </span>
                ))}
              </span>
              {canEdit && !isMobile && (
                <DateRangePicker
                  trip={trip}
                  initialDateRange={tripDateRange}
                  isOpen={isDatePickerOpen}
                  openDatePicker={setIsDatePickerOpen}
                  showEditBtn={isDateHovered || isDatePickerOpen}
                />
              )}
            </div>
          </ClickAwayListener>
        )}
      </div>
      {isMobile && canEdit && (
        <DateRangePicker
          trip={trip}
          initialDateRange={tripDateRange}
          isOpen={isDatePickerOpen}
          openDatePicker={setIsDatePickerOpen}
        />
      )}
      <div className='trip-details-container'>
        <div className='trip-details-inner-container'>
          {isMobile && userRoles && userRoles.length > 1 && (
            <LetterAvatarGroup userRoles={userRoles} />
          )}
        </div>
        {isMobile && Boolean(currentUser) && (
          <IconButton
            className='trip-title-menu-btn'
            label='Trip options'
            icon={<MoreHoriz />}
            tooltip={false}
            onClick={menu.open}
          />
        )}
      </div>
      {isMobile && Boolean(currentUser) && (
        <>
          <DropdownMenu {...menu.menuProps}>
            <DropdownMenu.Item
              hidden={!canEdit}
              icon={<EditOutlined />}
              deferUntilClosed
              onSelect={() => titleRef.current && startEditing(titleRef.current)}
            >
              Edit Trip Name
            </DropdownMenu.Item>
            <DropdownMenu.Item
              hidden={!canEdit}
              icon={<CalendarMonthOutlined />}
              onSelect={() => setIsDatePickerOpen(true)}
            >
              Edit Trip Dates
            </DropdownMenu.Item>
            <DropdownMenu.Item
              icon={<IosShare />}
              onSelect={() => setIsShareOpen(true)}
            >
              Share Trip
            </DropdownMenu.Item>
          </DropdownMenu>
          <TripShare
            trip={trip}
            canEdit={canEdit}
            open={isShareOpen}
            onClose={() => setIsShareOpen(false)}
          />
        </>
      )}
    </div>
  );
}

TripTitleHeader.displayName = 'TripTitleHeader';
export default TripTitleHeader;
